import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import ReviewPage from '../../shared/booking/ReviewPage';
import { ExpiredSelection } from '../../shared/booking/FlowParts';
import { checkoutBus } from '../../api/bus';
import { useAuth } from '../../auth/AuthContext';
import BusSummary from './BusSummary';
import { idTypes } from './data';
import { dateOf, timeLabel } from './searchModel';

/** Booking summary saved with the booking and shown by BusSummary. */
export function busSummary(review, search) {
  const { bus, seats, boardingPoint, droppingPoint } = review;
  const top = bus.cancellationPolicy?.[0];
  return {
    operator: bus.operator,
    busType: bus.busType,
    from: search.from,
    to: search.to,
    date: dateOf(bus.departure),
    departure: bus.departure,
    arrival: bus.arrival,
    duration: bus.durationMinutes,
    seats: seats.map((s) => s.id),
    seatDetails: seats.map((s) => ({ id: s.id, kind: s.kind, total: s.total })),
    boarding: boardingPoint,
    dropping: droppingPoint,
    cancellation: bus.cancellationPolicy || [],
    refundNote: top ? `Up to ${top.refundPercentage}% refund on cancellation` : null,
    partialCancellation: bus.partialCancellationAllowed,
    idProofRequired: bus.idProofRequired,
    mTicketAllowed: bus.mTicketAllowed,
    supplierCode: bus.supplierCode,
  };
}

/**
 * One passenger form per seat. Ladies-only seats can only be booked for women (and men-only seats
 * for men), so their title and gender options are limited; buses that need ID ask the primary passenger.
 */
export function passengerSpecs(review) {
  return review.seats.map((seat, i) => {
    const gender = seat.ladiesOnly ? ['Female'] : seat.menOnly ? ['Male'] : ['Male', 'Female'];
    const titles = seat.ladiesOnly ? ['Ms', 'Mrs'] : seat.menOnly ? ['Mr'] : ['Mr', 'Ms', 'Mrs'];
    const idFields = i === 0 && review.bus.idProofRequired ? ['idType', 'idNumber'] : [];
    return {
      type: 'Adult',
      label: `Seat ${seat.id}${i === 0 ? ' · Primary passenger' : ''}`,
      note: seat.ladiesOnly ? 'Ladies seat' : seat.menOnly ? 'Men only seat' : i === 0 ? 'Must be 18 or older' : undefined,
      fields: ['title', 'firstName', 'lastName', 'age', 'gender', ...idFields],
      options: { title: titles, gender, idType: idTypes },
      limits: { age: { min: i === 0 ? 18 : 1, max: 120 } },
    };
  });
}

export default function BusReview() {
  const { state } = useLocation();
  const { auth, signInWith } = useAuth();
  // The operator can raise the fare when it holds the seats; the page then shows and charges that fare.
  const [heldFare, setHeldFare] = useState(null);
  if (!state?.review || !state.search) return <ExpiredSelection module="bus" />;

  const { review, search, backTo } = state;
  const { bus } = review;
  const price = heldFare == null ? review.price : { ...review.price, taxes: heldFare - review.price.baseFare, fare: heldFare };
  const summary = busSummary(review, search);
  const specs = passengerSpecs(review);

  const submit = async ({ travellers, contact, fare, payment, promoCode, paymentId }) => {
    const result = await checkoutBus({
      reviewId: review.reviewId,
      contact: { email: contact.email.trim(), phone: contact.phone.trim() },
      passengers: travellers.map((t) => ({
        title: t.title,
        firstName: t.firstName.trim(),
        lastName: t.lastName.trim(),
        age: Number(t.age),
        gender: t.gender,
        idType: t.idType || null,
        idNumber: t.idNumber?.trim() || null,
      })),
      promoCode,
      // The API stops before booking if its fee or discount no longer gives this total.
      expectedTotal: fare.total,
      paymentMethod: payment,
      paymentId,
    }, auth?.accessToken).catch((error) => {
      if (error.status === 409 && error.details?.newFare != null) setHeldFare(Number(error.details.newFare));
      throw error;
    });

    if (result.auth) signInWith(result.auth);
    return {
      status: result.status === 'Confirmed' ? 'confirmed' : 'pending',
      ref: result.operatorPnr || result.supplierBookingId,
      supplierCode: bus.supplierCode,
      supplierBookingId: result.supplierBookingId,
      operatorPnr: result.operatorPnr || null,
      account: { status: result.accountStatus, email: result.accountEmail, emailSent: result.emailSent },
    };
  };

  const contactNote = (
    <p className="muted small form-note account-note">
      The operator may call or text this number about your bus, so use a 10-digit Indian mobile you'll have with you.
      {!auth?.accessToken && (
        <> Have a Trawtel account? <Link className="link small" to={`/account/login?returnTo=${encodeURIComponent(backTo)}`}>Sign in</Link> first,
          or we'll add this booking to the account for this email — and create one if it doesn't exist yet.</>
      )}
    </p>
  );

  return (
    <ReviewPage
      module="bus"
      heading="Review your bus booking"
      back={{ to: backTo, label: 'Change seats' }}
      summary={<BusSummary summary={summary} />}
      info={[
        `Reach ${summary.boarding.name} at least 15 minutes before ${summary.boarding.time ? timeLabel(summary.boarding.time) : 'departure'}.`,
        'Carry a valid government photo ID for every passenger.',
        bus.idProofRequired ? 'This operator checks the ID proof given for the primary passenger.' : 'Names must match the ID you carry.',
        bus.partialCancellationAllowed ? 'You can cancel some seats and keep others.' : 'All seats on this ticket are cancelled together.',
      ]}
      travellers={specs}
      travelDate={summary.date}
      travelEnd={dateOf(bus.arrival) || summary.date}
      fareLines={[[`Base fare · ${review.seats.length} seat${review.seats.length > 1 ? 's' : ''}`, price.baseFare], ['Taxes', price.taxes, 'tax']]}
      showGst={false}
      onSubmit={submit}
      submitNotice="Seats are booked with the operator on confirmation."
      initialContact={auth?.user?.email ? { email: auth.user.email } : undefined}
      contactNote={contactNote}
      buildBooking={() => ({
        title: `${summary.from} → ${summary.to} · ${summary.operator}`,
        subtitle: `${timeLabel(summary.departure)} departure · Seat${summary.seats.length > 1 ? 's' : ''} ${summary.seats.join(', ')}`,
        travelDate: summary.date,
        endDate: dateOf(summary.arrival) || summary.date,
        summary,
      })}
    />
  );
}
